import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWebSocket } from '../context/WebSocketContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Car, User, LogOut, History, Settings, Menu, ShoppingBag, Package, ShoppingCart, CreditCard, Home, ChevronLeft, Camera, FileText, Wifi, WifiOff, Shield, Wrench, Truck, Crown, Monitor, Smartphone, Briefcase, Utensils } from 'lucide-react';
import { useState } from 'react';
import { ViewModeToggle, useMobileView } from './MobileLayout';
import { NotificationSoundToggle } from './NotificationSoundSettings';
import NotificationBell from './NotificationBell';
import EditableText from './EditableText';
import EditableImage from './EditableImage';

// Account type badge configuration
const accountTypeBadges = {
  customer: { label: 'Customer', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: User },
  mechanic: { label: 'Mechanic', color: 'bg-green-100 text-green-700 border-green-300', icon: Wrench },
  roadside_tech: { label: 'Roadside Tech', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: Truck },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: Shield },
  owner: { label: 'Owner', color: 'bg-yellow-100 text-yellow-800 border-yellow-400', icon: Crown }
};

export function AccountTypeBadge({ userType, isAdmin, isOwner, size = 'sm' }) {
  // Determine the display type (owner takes precedence)
  let displayType = userType;
  if (isOwner) displayType = 'owner';
  else if (isAdmin && userType !== 'owner') displayType = 'admin';
  
  const config = accountTypeBadges[displayType] || accountTypeBadges.customer;
  const Icon = config.icon;
  
  const sizeClasses = size === 'sm' 
    ? 'text-xs px-2 py-0.5' 
    : size === 'md' 
    ? 'text-sm px-2.5 py-1' 
    : 'text-base px-3 py-1.5';
  
  return (
    <span 
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${config.color} ${sizeClasses}`}
      data-testid="account-type-badge"
    >
      <Icon className={size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5'} />
      {config.label}
    </span>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { isConnected, connectionStatus } = useWebSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Owner can access all dashboards
  const getDashboardPath = () => {
    if (user?.user_type === 'owner' || user?.is_owner) {
      return '/owner'; // Owner defaults to owner command center
    }
    // Providers go to the new provider dashboard
    if (user?.user_type === 'mechanic' || user?.user_type === 'roadside_tech') {
      return '/provider';
    }
    return '/customer';
  };
  
  const dashboardPath = getDashboardPath();
  
  // Determine if we should show back button (not on landing, login, register, or dashboard pages)
  const showBackButton = user && !['/customer', '/mechanic', '/admin', '/'].includes(location.pathname);
  const isHomePage = location.pathname === '/' || location.pathname === dashboardPath;

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-gray-200/50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left Section: Navigation Buttons + Logo */}
          <div className="flex items-center gap-2">
            {/* Global Back Button */}
            {showBackButton && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-900"
                data-testid="header-back-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
            )}
            
            {/* Global Home Button */}
            {user && !isHomePage && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(dashboardPath)}
                className="text-gray-600 hover:text-gray-900"
                data-testid="header-home-btn"
              >
                <Home className="w-5 h-5" />
              </Button>
            )}
            
            {/* Logo */}
            <Link to={user ? dashboardPath : '/'} className="flex items-center gap-2.5" data-testid="logo-link">
              <EditableImage
                brandKey="logo_image_url"
                imgClassName="w-10 h-10 rounded-xl object-contain"
                fallback={
                  <div className="w-10 h-10 bg-[#FF3008] rounded-xl flex items-center justify-center shadow-sm">
                    <EditableText
                      brandKey="logo_letter"
                      defaultValue="J"
                      className="text-white font-extrabold text-lg"
                      testId="header-logo-letter"
                    />
                  </div>
                }
                testId="header-logo-image"
              />
              <div className="hidden sm:flex flex-col">
                <EditableText
                  brandKey="app_name"
                  defaultValue="Jiffys"
                  className="text-lg font-extrabold text-gray-900 leading-none"
                  testId="header-app-name"
                />
                <EditableText
                  brandKey="app_tagline"
                  defaultValue="ASSISTANCE"
                  className="text-[10px] font-medium text-gray-500 tracking-wide uppercase"
                  testId="header-app-tagline"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/marketplace" 
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              data-testid="nav-marketplace"
            >
              Marketplace
            </Link>
            <Link 
              to="/food" 
              className="text-[#FF3008] hover:text-[#D42600] font-medium transition-colors"
              data-testid="nav-food"
            >
              Get it Delivered
            </Link>
            
            {user ? (
              <>
                <Link 
                  to={dashboardPath} 
                  className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Link>
                {(user.is_owner || user.user_type === 'owner') && (
                  <Link 
                    to="/owner" 
                    className="text-purple-600 hover:text-purple-700 font-semibold transition-colors"
                    data-testid="nav-owner"
                  >
                    Owner Command
                  </Link>
                )}
                {(user.user_type === 'mechanic' || user.user_type === 'roadside_tech') && (
                  <Link 
                    to="/provider" 
                    className="text-green-600 hover:text-green-700 font-semibold transition-colors"
                    data-testid="nav-provider-dashboard"
                  >
                    My Jobs
                  </Link>
                )}
                {user.user_type === 'customer' && (
                  <Link 
                    to="/customer/history" 
                    className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
                    data-testid="nav-history"
                  >
                    History
                  </Link>
                )}
                
                {/* Cart Icon */}
                <Link to="/cart" className="relative" data-testid="nav-cart">
                  <ShoppingCart className="w-6 h-6 text-gray-600 hover:text-gray-900" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
                
                {/* Notification Bell for Admins/Owners */}
                <NotificationBell />
                
                {/* Sound Toggle - Easy access for customers */}
                {user.user_type === 'customer' && (
                  <NotificationSoundToggle />
                )}
                
                <button
                  onClick={handleLogout}
                  className="text-red-600 hover:text-red-700 font-medium transition-colors"
                  data-testid="nav-logout"
                >
                  Logout
                </button>
                
                {/* User Profile Dropdown with Edit Overlay */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 relative group" data-testid="user-menu-trigger">
                      <div className="relative">
                        <div className="w-9 h-9 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden border-2 border-gray-300 group-hover:border-blue-400 transition-colors">
                          {user.picture ? (
                            <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        {/* Edit overlay badge */}
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="w-2.5 h-2.5 text-white" />
                        </div>
                        {/* WebSocket connection indicator */}
                        <div 
                          className={`absolute -top-0.5 -left-0.5 w-3 h-3 rounded-full border-2 border-white ${
                            isConnected ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                          title={isConnected ? 'Real-time connected' : 'Connecting...'}
                        />
                      </div>
                      <div className="hidden lg:flex flex-col items-start">
                        <span className="text-gray-700 font-medium">{user.name}</span>
                        <AccountTypeBadge 
                          userType={user.user_type} 
                          isAdmin={user.is_admin} 
                          isOwner={user.is_owner} 
                          size="sm" 
                        />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Profile Picture Section */}
                    <div className="px-3 py-2 border-b">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                          {user.picture ? (
                            <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{user.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          <AccountTypeBadge 
                            userType={user.user_type} 
                            isAdmin={user.is_admin} 
                            isOwner={user.is_owner} 
                            size="sm" 
                          />
                        </div>
                      </div>
                      <button 
                        onClick={() => navigate('/settings')}
                        className="mt-2 w-full text-center text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1"
                        data-testid="edit-profile-picture-btn"
                      >
                        <Camera className="w-3 h-3" /> Edit Profile Picture
                      </button>
                    </div>
                    
                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/marketplace/my-listings')} data-testid="menu-my-listings">
                      <Package className="w-4 h-4 mr-2" />
                      My Listings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile/setup')} data-testid="menu-profile-setup">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payment & Profile
                    </DropdownMenuItem>
                    {['mechanic', 'roadside_tech', 'admin', 'owner'].includes(user.user_type) && (
                      <DropdownMenuItem onClick={() => navigate('/employee/settings')} data-testid="menu-employee-settings">
                        <FileText className="w-4 h-4 mr-2" />
                        Employee Settings
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate(dashboardPath)} data-testid="menu-dashboard">
                      <Home className="w-4 h-4 mr-2" />
                      Dashboard
                    </DropdownMenuItem>
                    {['mechanic', 'roadside_tech'].includes(user.user_type) && (
                      <DropdownMenuItem onClick={() => navigate('/provider')} data-testid="menu-provider-dashboard">
                        <Briefcase className="w-4 h-4 mr-2" />
                        My Jobs & Earnings
                      </DropdownMenuItem>
                    )}
                    {user.user_type === 'customer' && (
                      <DropdownMenuItem onClick={() => navigate('/customer/history')} data-testid="menu-history">
                        <History className="w-4 h-4 mr-2" />
                        Service History
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    
                    {/* View Mode Toggle */}
                    <div className="px-2 py-2">
                      <p className="text-xs text-gray-500 mb-2 px-2">View Mode</p>
                      <ViewModeToggle />
                    </div>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
                  data-testid="nav-login"
                >
                  Login
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200" data-testid="mobile-menu">
            {user ? (
              <div className="space-y-3">
                {/* Mobile Profile Section */}
                <div className="px-4 py-3 bg-gray-50 rounded-lg mb-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-gray-300">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <button 
                        onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}
                        className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white"
                        data-testid="mobile-edit-profile-btn"
                      >
                        <Camera className="w-3 h-3 text-white" />
                      </button>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500 mb-1">{user.email}</p>
                      <AccountTypeBadge 
                        userType={user.user_type} 
                        isAdmin={user.is_admin} 
                        isOwner={user.is_owner} 
                        size="sm" 
                      />
                    </div>
                  </div>
                </div>
                
                <Link 
                  to="/marketplace"
                  className="flex items-center gap-2 px-4 py-2 text-blue-600 font-semibold hover:bg-blue-50 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-marketplace"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Marketplace
                </Link>
                <Link 
                  to="/food"
                  className="flex items-center gap-2 px-4 py-2 text-orange-600 font-semibold hover:bg-orange-50 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-food"
                >
                  <Utensils className="w-5 h-5" />
                  Get it Delivered
                </Link>
                <Link 
                  to="/cart"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ShoppingCart className="w-5 h-5" />
                  Cart {cartCount > 0 && `(${cartCount})`}
                </Link>
                <Link 
                  to={dashboardPath}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Home className="w-5 h-5" />
                  Dashboard
                </Link>
                {(user.user_type === 'mechanic' || user.user_type === 'roadside_tech') && (
                  <Link 
                    to="/provider"
                    className="flex items-center gap-2 px-4 py-2 text-green-600 font-semibold hover:bg-green-50 rounded"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Briefcase className="w-5 h-5" />
                    My Jobs & Earnings
                  </Link>
                )}
                <Link 
                  to="/settings"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-settings"
                >
                  <Settings className="w-5 h-5" />
                  Settings
                </Link>
                {user.user_type === 'customer' && (
                  <Link 
                    to="/customer/history"
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <History className="w-5 h-5" />
                    History
                  </Link>
                )}
                <Link 
                  to="/marketplace/my-listings"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Package className="w-5 h-5" />
                  My Listings
                </Link>
                <Link 
                  to="/marketplace/sell"
                  className="flex items-center gap-2 px-4 py-2 text-green-600 font-semibold hover:bg-green-50 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sell Something
                </Link>
                <button 
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100 rounded"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <Link 
                  to="/marketplace"
                  className="flex items-center gap-2 px-4 py-2 text-blue-600 font-semibold hover:bg-blue-50 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ShoppingBag className="w-5 h-5" />
                  Marketplace
                </Link>
                <Link 
                  to="/food"
                  className="flex items-center gap-2 px-4 py-2 text-orange-600 font-semibold hover:bg-orange-50 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Utensils className="w-5 h-5" />
                  Get it Delivered
                </Link>
                <Link 
                  to="/login"
                  className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/register"
                  className="block px-4 py-2 text-blue-600 font-semibold hover:bg-gray-100 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
